/**
 * Compact weight-dispute card for the merchant shipment-details page.
 *
 * Renders the dispute(s) already embedded in the order-tracking history response — no API call of
 * its own. Empty array → renders nothing (no clutter on orders without a dispute). The full
 * consent flow lives on /weight-disputes/{disputeId}; this card's job is to surface "action
 * needed" and the headline numbers.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Clock, ArrowRight } from "lucide-react";
import {
  MoneyAmount,
  StatusPill,
  ChargeStatusPill,
  formatGrams,
  formatSignedGrams,
  formatDateTime,
} from "./wdUi";

function DisputeCard({ dispute }) {
  const navigate = useNavigate();
  const isOpen = dispute.status === "OPEN";
  const amount = dispute.extraChargeAmount ?? 0;

  return (
    <div className={`rounded-2xl border bg-white p-4 shadow-sm ${isOpen ? "border-amber-300" : "border-gray-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Scale className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Weight Discrepancy</h3>
          <StatusPill status={dispute.status} />
          <ChargeStatusPill chargeStatus={dispute.chargeStatus} />
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-gray-600">
            Revision {dispute.revisionNumber ?? 1}
          </span>
        </div>
        <button
          onClick={() => navigate(`/weight-disputes/${dispute.disputeId}`)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
            isOpen
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
          }`}
        >
          {isOpen ? "Review & Respond" : "View Details"}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Carrier Weight</div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-gray-800">
            {formatGrams(dispute.carrierWeightGrams)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Charged Weight</div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-gray-800">
            {formatGrams(dispute.declaredWeightSnapshot?.chargedWeightGrams)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Variance</div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-amber-700">
            {formatSignedGrams(dispute.varianceGrams)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {amount < 0 ? "Refund Due" : "Extra Charge"}
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums">
            <MoneyAmount amount={amount} />
          </div>
        </div>
      </div>

      {isOpen && dispute.autoAcceptOnDateTime && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          <Clock className="h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
          <span>
            Action needed — this charge auto-accepts on{" "}
            <span className="font-semibold">{formatDateTime(dispute.autoAcceptOnDateTime)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default function OrderWeightDisputeCard({ disputes }) {
  if (!disputes || disputes.length === 0) return null;
  return (
    <div className="space-y-3">
      {disputes.map((dispute, index) => (
        <DisputeCard key={dispute.disputeId ?? index} dispute={dispute} />
      ))}
    </div>
  );
}
